import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionBar, ActionButton } from './ActionButton.js';
import { Alert } from './feedback.js';
import { Form } from './Form.js';
import { PageHeader, type Crumb } from './layout.js';
import { saveProblems, useSaveErrors } from './save.js';
import { focusFirstInvalid, useSaveFailureFocus } from './server-form.js';

/**
 * The standard form page: header, a bounded column of titled sections, and
 * one footer with Cancel and Save.
 *
 * THE SHAPE, ONCE. A form is read top to bottom and saved at the bottom, so
 * the column is capped (`max-w-4xl`) rather than stretched across a wide
 * screen where the eye loses the line; each `FormSection` inside is a card
 * whose fields sit two to a row from the `sm` breakpoint and one to a row on
 * a phone. Nothing here fixes a width in pixels, so a long Arabic label wraps.
 *
 * SAVING. While the save is in flight the Save button spins and a second
 * submit is ignored (`Form` refuses it). When it fails the summary at the top
 * names what went wrong in the user's language — every failure, not a chosen
 * few — and the FIRST FIELD MARKED INVALID is scrolled to and focused; a
 * failure that names no field (network, timeout, 500) focuses the summary
 * instead. The fields keep what was typed: a failed save never resets,
 * refetches over or leaves the form (D205). See `save.ts` for the rules.
 */
export interface FormPageProps {
  /** Already translated. */
  readonly title: string;
  readonly subtitle?: string;
  readonly breadcrumbs?: readonly Crumb[];
  /** Header actions that are not the save itself (deactivate, archive). */
  readonly actions?: ReactNode;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
  readonly isSubmitting: boolean;
  /** False for a reader without the permission: the form shows, Save does not. */
  readonly canSubmit?: boolean;
  /** Translated. Defaults to "Save". */
  readonly submitLabel?: string;
  /** The last failed save, if any. */
  readonly error?: unknown;
  /** Field name to its translated label, so the summary can name the field. */
  readonly fieldLabels?: Readonly<Record<string, string>>;
  /** Anything the page must say above the form (a banner, a warning). */
  readonly notices?: ReactNode;
  /** A separate act below the form, outside it (withdraw, archive). */
  readonly after?: ReactNode;
  readonly children: ReactNode;
}

/**
 * A failed save, said in the user's language: the headline from the API's
 * error code and every problem it named. For dialogs and panels that save
 * without a whole page; `FormPage` uses the same notice.
 *
 * With a `title` of its own ("Settings could not be saved.") the API's
 * headline moves into the description, ahead of the problems.
 */
export function SaveErrorNotice({
  error,
  fieldLabels = {},
  fieldMessages = {},
  title,
}: {
  readonly error: unknown;
  readonly fieldLabels?: Readonly<Record<string, string>>;
  /** A field's own sentence for a format rule, as the field shows it. */
  readonly fieldMessages?: Readonly<Record<string, string>>;
  /** Translated. */
  readonly title?: string;
}) {
  const { t } = useTranslation();
  const { message } = useSaveErrors(error);
  if (!message) return null;
  const problems = saveProblems(error, t, fieldLabels, fieldMessages);
  const description = title ? [message, ...problems] : problems;
  return (
    <Alert
      tone="danger"
      title={title ?? message}
      {...(description.length > 0 ? { description: description.join(' · ') } : {})}
    />
  );
}

export function FormPage({
  title,
  subtitle,
  breadcrumbs,
  actions,
  onSubmit,
  onCancel,
  isSubmitting,
  canSubmit = true,
  submitLabel,
  error,
  fieldLabels = {},
  notices,
  after,
  children,
}: FormPageProps) {
  const { t } = useTranslation();
  const { message } = useSaveErrors(error);
  const summaryRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [attempts, setAttempts] = useState(0);

  // A server refusal: the first field it named, or the summary when it named
  // none. A failure at the bottom of a long form would otherwise be invisible.
  useSaveFailureFocus(error, formRef, summaryRef);

  // A submit the page itself refused (its own check found a problem): the
  // first field it marked. Nothing marked means the save went ahead.
  useEffect(() => {
    if (attempts > 0) focusFirstInvalid(formRef.current);
  }, [attempts]);


  return (
    <>
      <PageHeader
        title={title}
        {...(subtitle ? { subtitle } : {})}
        {...(breadcrumbs ? { breadcrumbs } : {})}
        {...(actions ? { actions } : {})}
      />
      <div className="flex w-full max-w-4xl flex-col gap-gutter">
        {notices}
        {message ? (
          <div ref={summaryRef} tabIndex={-1} className="outline-none">
            <SaveErrorNotice error={error} fieldLabels={fieldLabels} />
          </div>
        ) : null}
        <div ref={formRef} className="contents">
        <Form
          onSubmit={() => {
            setAttempts((count) => count + 1);
            onSubmit();
          }}
          isSubmitting={isSubmitting}
          actions={
            <div className="border-t border-default-200 pt-stack">
              <ActionBar>
                <ActionButton
                  priority="tertiary"
                  onPress={onCancel}
                  isDisabled={isSubmitting}
                >
                  {t('common.cancel')}
                </ActionButton>
                {canSubmit ? (
                  <ActionButton
                    priority="primary"
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    {submitLabel ?? t('common.save')}
                  </ActionButton>
                ) : null}
              </ActionBar>
            </div>
          }
        >
          {children}
        </Form>
        </div>
        {after}
      </div>
    </>
  );
}
